import React, { useEffect, useRef, useState } from 'react';

const faqs = [
  {
    question: 'What types of businesses do you work with?',
    answer:
      'We work with B2B service businesses, coaches, consultants, SaaS companies, and e-commerce brands — any business that sells high-intent products or services and wants predictable client acquisition. We serve clients globally, with a strong presence across India, Southeast Asia, and Western markets.',
  },
  {
    question: 'How quickly can I expect to see results?',
    answer:
      'Most clients see their first qualified leads within 14–21 days of launch. Meaningful pipeline growth typically occurs within the first 30–60 days, with compounding results as we optimize campaigns and automate follow-up workflows.',
  },
  {
    question: 'What platforms do you run ads on?',
    answer:
      'We run campaigns across Meta (Facebook & Instagram), Google Ads, LinkedIn, and YouTube — whichever channel your ideal customer is most active on. Our approach is data-led: we test, measure, and scale the best-performing channels for your specific offer.',
  },
  {
    question: 'Do you offer AI automation as a standalone service?',
    answer:
      'Yes. Our CRM & AI Automation service can be implemented independently. We set up platforms like GoHighLevel, HubSpot, or custom n8n/Make.com workflows — including AI chatbots, lead nurturing sequences, and pipeline automation — even if you manage your own lead generation.',
  },
  {
    question: 'Where is SGS Global Media based?',
    answer:
      "We're headquartered at Balewadi High Street, Pune, Maharashtra, India. We serve clients globally, operating across time zones to support businesses in India, the UAE, UK, US, and beyond.",
  },
];

const FAQ = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [openIndex, setOpenIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="faq" className="py-24 px-6 bg-[#020617] relative overflow-hidden" ref={sectionRef}>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .faq-animate {
          opacity: 0;
        }
        .faq-animate.visible {
          animation: fadeInUp 0.7s ease-out forwards;
        }
        .faq-answer {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.4s ease, padding 0.3s ease;
        }
        .faq-answer.open {
          max-height: 300px;
          padding-bottom: 1.25rem;
        }
        .faq-toggle {
          transition: transform 0.3s ease, background 0.2s ease;
        }
        .faq-toggle.open {
          transform: rotate(45deg);
          background: rgba(0,123,255,0.8);
        }
      `}</style>

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">

          {/* Left — heading */}
          <div className={`faq-animate ${isVisible ? 'visible' : ''}`}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#007bff] mb-3">FAQ</p>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight mb-4">
              Frequently<br />Asked Questions
            </h2>
            <p className="text-gray-400 text-lg max-w-sm">
              Everything you need to know before working with us.
            </p>
          </div>

          {/* Right — accordion */}
          <div
            className={`faq-animate ${isVisible ? 'visible' : ''} space-y-0`}
            style={isVisible ? { animationDelay: '0.15s' } : {}}
          >
            {faqs.map((faq, index) => {
              const isOpen = openIndex === index;
              return (
                <div key={index} className="border-b border-white/5">
                  <button
                    className="w-full py-5 flex justify-between items-center gap-4 text-left group"
                    onClick={() => setOpenIndex(isOpen ? -1 : index)}
                    aria-expanded={isOpen}
                  >
                    <span
                      className={`font-semibold text-base transition-colors duration-200 ${
                        isOpen ? 'text-[#007bff]' : 'text-white group-hover:text-[#3399ff]'
                      }`}
                    >
                      {faq.question}
                    </span>
                    <span
                      className={`faq-toggle flex-shrink-0 w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-white text-lg font-light ${
                        isOpen ? 'open' : ''
                      }`}
                    >
                      +
                    </span>
                  </button>
                  <div className={`faq-answer ${isOpen ? 'open' : ''}`}>
                    <p className="text-gray-400 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
